import type { Recipe } from '../types';

// The easy one: ground beef, a bag or two of frozen mixed vegetables, canned
// tomatoes and beef broth in one pot. About 15 minutes of actual work — the
// only knife job is an onion and two potatoes. Everything else is dump and stir.

export const hamburgerVegetableSoup: Recipe = {
  slug: 'hamburger-vegetable-soup',
  title: 'Hamburger Vegetable Soup',
  meal: 'dinner',
  category: 'meal',
  types: ['soup'],
  cuisines: ['american'],
  traits: ['easy-prep', 'freezer-friendly', 'one-pot', 'make-ahead', 'bulk'],
  yield: 'about 18 cups — 9 servings',
  activeTime: '15 min',
  totalTime: '40 min',
  freezerLife: '3 months',
  difficulty: 1,

  summary:
    "Old-school hamburger soup. Brown the beef with an onion, dump in canned tomatoes, beef broth, diced potatoes and frozen mixed vegetables, and let it simmer. About 15 minutes of hands-on work for a big pot that freezes perfectly.",

  flavorNotes:
    "Homey and savory — rich beef broth, sweet tomato, tender vegetables in every spoonful, with a little depth from Worcestershire and dried herbs. Like vegetable beef soup from a diner, only better. It should taste beefy, not like tomato juice; if it seems flat at the end, it almost always needs more salt. Not spicy at all.",

  ingredients: [
    {
      id: 'beef',
      name: 'Ground beef',
      total: '2 lb',
      prep: 'thawed in the fridge overnight',
      fromFreezer: true,
      uses: [{ step: 1, amount: '2 lb' }],
    },
    {
      id: 'onion',
      name: 'Yellow onion',
      total: '1 large',
      prep: 'diced',
      shop: 'Fresh yellow onion, each (1)',
      uses: [{ step: 1, amount: '1 large' }],
    },
    {
      id: 'garlic',
      name: 'Garlic',
      total: '4 cloves',
      prep: 'minced',
      uses: [{ step: 2, amount: '4 cloves' }],
    },
    {
      id: 'italian-seasoning',
      name: 'Italian seasoning',
      total: '2 tsp',
      uses: [{ step: 2, amount: '2 tsp' }],
    },
    {
      id: 'salt',
      name: 'Salt',
      total: '1½ tsp',
      uses: [{ step: 2, amount: '1½ tsp', note: 'plus more to taste at the end' }],
    },
    {
      id: 'pepper',
      name: 'Black pepper',
      total: '½ tsp',
      uses: [{ step: 2, amount: '½ tsp' }],
    },
    {
      id: 'beef-base',
      name: 'Better Than Bouillon Organic Roasted Beef base',
      total: '2 tbsp',
      shop: 'Better Than Bouillon Organic Roasted Beef base, 8 oz jar',
      uses: [{ step: 3, amount: '2 tbsp', note: 'makes 6 cups of broth with the water' }],
    },
    {
      id: 'water',
      name: 'Water',
      total: '6 cups',
      uses: [{ step: 3, amount: '6 cups' }],
    },
    {
      id: 'tomatoes',
      name: 'Diced tomatoes, 28 oz can',
      total: '1 can',
      prep: 'undrained — Dei Fratelli or Red Gold',
      uses: [{ step: 3, amount: '1 can' }],
    },
    {
      id: 'crushed-tomatoes',
      name: 'Crushed tomatoes, 28 oz can',
      total: '1 can',
      prep: 'Red Gold or Dei Fratelli',
      uses: [{ step: 3, amount: '1 can' }],
    },
    {
      id: 'worcestershire',
      name: 'Worcestershire sauce',
      total: '1 tbsp',
      uses: [{ step: 3, amount: '1 tbsp' }],
    },
    {
      id: 'potatoes',
      name: 'Russet potatoes',
      total: '2 medium',
      prep: 'peeled and cut into small ½-inch cubes',
      shop: 'Russet potatoes, loose, each (2)',
      uses: [{ step: 3, amount: '2 medium' }],
    },
    {
      id: 'mixed-veg',
      name: 'Frozen mixed vegetables',
      total: '5 cups',
      prep: 'straight from frozen — corn, peas, carrots, green beans',
      shop: 'Great Value frozen mixed vegetables, 12 oz bag (2)',
      uses: [{ step: 4, amount: '5 cups', note: 'two 12 oz bags' }],
    },
    {
      id: 'parmesan',
      name: 'BelGioioso Parmesan',
      total: 'for serving',
      prep: 'grated',
      optional: true,
      optionalKind: 'garnish',
      optionalWhy: 'A spoonful on top makes it taste richer. Skip it and the soup is still complete.',
      uses: [{ step: 5, amount: 'a sprinkle', note: 'at the table' }],
    },
  ],

  steps: [
    {
      n: 1,
      title: 'Brown the beef and onion',
      time: '8 min',
      ingredientIds: ['beef', 'onion'],
      body:
        "In a big soup pot (at least 7 quarts) over medium-high, cook the {2 lb|beef} of ground beef and the diced onion, breaking the beef into small crumbles, until no pink is left and the onion is soft, about 8 minutes. Spoon off most of the fat if there is a lot pooled in the pot.",
    },
    {
      n: 2,
      title: 'Garlic and seasoning',
      time: '1 min',
      ingredientIds: ['garlic', 'italian-seasoning', 'salt', 'pepper'],
      body:
        "Stir in the garlic, {2 tsp|italian-seasoning} of Italian seasoning, {1½ tsp|salt} of salt and {½ tsp|pepper} of pepper for 1 minute, until it smells good.",
    },
    {
      n: 3,
      title: 'Dump in the liquids and potatoes, simmer',
      time: '12 min',
      critical: true,
      ingredientIds: ['beef-base', 'water', 'tomatoes', 'crushed-tomatoes', 'worcestershire', 'potatoes'],
      body:
        "Add {6 cups|water} of water and stir in {2 tbsp|beef-base} of Better Than Bouillon until it dissolves. Add {1 can|tomatoes} of diced tomatoes with their juice, {1 can|crushed-tomatoes} of crushed tomatoes, {1 tbsp|worcestershire} of Worcestershire and the cubed potatoes. Bring it to a boil, then turn it down to a simmer, partly covered, for 10 minutes.",
      tip:
        "Cut the potatoes small — ½ inch. Small cubes cook in the same time as the frozen vegetables and hold up better in the freezer than big chunks.",
    },
    {
      n: 4,
      title: 'Add the frozen vegetables',
      time: '12 min',
      ingredientIds: ['mixed-veg'],
      body:
        "Stir in {5 cups|mixed-veg} of frozen mixed vegetables right from the bag. Bring it back to a simmer and cook 10 to 12 minutes more, until a potato cube smashes easily against the side of the pot with a spoon.",
    },
    {
      n: 5,
      title: 'Taste and serve',
      ingredientIds: ['parmesan'],
      body:
        "Taste a spoonful and add salt a pinch at a time until it tastes beefy and full — this is the step that makes it good. Serve with Parmesan on top, and cornbread or a hoagie roll on the side.",
    },
  ],

  freezing:
    "Cool completely (spread it into a few shallow containers to cool faster). Ladle into quart freezer bags or containers, about 2 servings each, leaving an inch of room at the top. Freeze flat up to 3 months. The potatoes soften a little after freezing — still good.",

  reheat: [
    {
      method: 'Stovetop',
      best: true,
      detail:
        "Thaw overnight in the fridge, then heat in a pot over medium until it simmers, about 8 minutes. From frozen: run the bag under warm water to loosen, slide the block into a covered pot with a splash of water, and heat on medium-low, stirring as it melts, about 20 minutes.",
    },
    {
      method: 'Microwave',
      detail:
        "Thawed, in a covered bowl, 2 to 3 minutes per serving, stirring halfway.",
    },
  ],

  glossary: [
    {
      term: 'simmer',
      plain:
        "Small, gentle bubbles breaking the surface — lower than a full rolling boil. A hard boil smashes the vegetables apart; a simmer keeps them whole.",
    },
    {
      term: 'Better Than Bouillon',
      plain:
        "A concentrated broth paste in a jar. Stir 1 teaspoon into 1 cup of hot water and you have broth. Cheaper and less salty than cartons, and one jar makes about 38 cups.",
    },
  ],

  inspiration: [
    { label: 'Hostess At Heart — Easy hamburger vegetable soup', url: 'https://hostessatheart.com/hamburger-vegetable-soup/' },
    { label: 'Happi Homemade — Easy hamburger soup', url: 'https://happihomemade.com/hamburger-soup/' },
  ],

  whereWeVeered:
    "Both sources are 1 lb recipes for five or six. We doubled the beef to 2 lb for a freezer-sized pot. Took Happi Homemade's potatoes and Worcestershire, and Hostess At Heart's Better Than Bouillon and frozen mixed vegetables, but used crushed tomatoes in place of canned tomato sauce (fewer ingredients on the label) and skipped the bay leaf and the extra garlic and onion powder — the fresh onion and garlic cover it. Potatoes are cut small and go in first so the whole pot finishes at the same time.",
};
